import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import usePageMeta from '@/hooks/usePageMeta';
import { categories, getArticlesByCategory, getCategoryBySlug, hotArticles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function CategoryDetail() {
  const { slug = '' } = useParams();
  const category = getCategoryBySlug(slug);
  const list = category ? getArticlesByCategory(category.slug) : [];

  usePageMeta({
    title: category
      ? `${category.name} - 看料99 独家内幕与第一手消息`
      : '分类不存在 - 看料99',
    description: category
      ? `${category.name}是看料99 的重点栏目之一。${category.desc}`
      : '该分类暂不可用。',
    keywords: category
      ? `看料99${category.name},看料99第一手消息,看料99抢先看,${category.name}`
      : '看料99',
    path: `/category/${slug}`,
    jsonLd: category
      ? {
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name} - 看料99`,
          description: category.desc,
          url: `https://lfthg.cn/category/${category.slug}`,
        }
      : undefined,
  });

  if (!category) {
    return <NotFound />;
  }

  return (
    <SiteLayout>
      <div className="animate-fade-up">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '全部分类', to: '/category' },
                { label: category.name },
              ]}
            />
            <div className="mt-4 flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500 text-background-50 dark:text-foreground-950">
                <i className={`${category.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <div>
                <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                  {category.name}
                </h1>
                <p className="mt-1 text-sm text-foreground-500">共 {list.length} 篇内容</p>
              </div>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {category.desc}
            </p>
          </div>
        </section>

        <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-[1.8fr_1fr]">
          <div>
            {list.length > 0 ? (
              <div className="space-y-5">
                {list.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="horizontal" />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
                <p className="text-sm text-foreground-600">该分类内容正在筹备中，敬请期待。</p>
              </div>
            )}
          </div>

          <aside className="space-y-8">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-1.5 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
                全站热榜
              </h2>
              <ol className="mt-3 space-y-1">
                {hotArticles.map((item, index) => (
                  <li key={item.id}>
                    <Link
                      to={`/article/${item.id}`}
                      className="group flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
                    >
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-bold ${
                          index < 3
                            ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                            : 'bg-background-300 text-foreground-700'
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">切换分类</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/category/${c.slug}`}
                    className={`rounded-full px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                      c.slug === category.slug
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'border border-background-300 text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}